import type { HowRelayWorksReference } from "./types";

export const howDoIReference: HowRelayWorksReference = {
  slug: "how-do-i",
  name: "How do I…",
  oneLiner: "Step-by-step answers to common tasks.",
  sections: [
    {
      title: "How do I get the Relay daemon running on my computer?",
      items: [
        "Run the Relay helper without Terminal: install Relay Helper from the .dmg and open it. A short setup asks for your key (Relay → Team → My Key), finds your projects' folders on this Mac, then checks everything. After that it lives in the menu bar: click its icon to see what your AI partners are doing. If a full menu bar hides it, open Relay Helper's Settings to start or stop it.",
        "Once your key is accepted, Relay Helper lists your Relay projects and finds each one's folder on this Mac from the project's repo. Confirm each match, or choose Locate… or Clone. The folder is saved for this Mac only, so another computer can keep its own.",
        "Install it: npm install -g @easyrelay/daemon. You need Node 18 or newer and one signed-in AI command-line tool (claude, gemini or codex).",
        "In Team → My Key, click \"Create My Key\" (or \"Create New Key…\" to replace one) and copy the key right away. It's shown once.",
        "In a terminal, run: relay install --secret <your key> --url https://easyrelay.app --project-root <top folder of your project's git repo>. It must be the repo's top folder, not a subfolder.",
        "Run relay start and leave that terminal open. Closing it, or your computer sleeping, stops the daemon.",
        "Check Team → Relay Helper. It should say \"Running\".",
        "\"Relay Helper is offline\" on that card means the helper isn't running: open Relay Helper and click Start, or run relay start. Sprint Planning shows \"Daemon offline\" too, and the Lock button stays disabled until it's back.",
        "More: /#set-up",
      ],
    },
    {
      title: "How do I connect an AI partner, and choose which ones the Sprint Board uses?",
      items: [
        "Open Team → AI Partners and click \"+ Add\".",
        "Type the partner's ID (for example dev-5). No spaces, and none of / \\ ~ ^ : ? * [ .",
        "Click \"Add\". That puts the name on your roster; it doesn't create a key.",
        "Run relay start (or restart the daemon) so the partner can receive work.",
        "To connect an AI chat such as Claude.ai, use Team → Connect an AI chat. Your personal connection URL (it acts as you) appears once, in Team → My Key, right after you create or replace your key; in Claude.ai, open Connectors in Settings, choose Add custom connector, and paste it. Relay no longer shows a shared workspace URL.",
        "If your AI partner gets 'Not found' or 'Too many failed attempts', one of your connections is using an old key. Remove it in your AI tool's connector settings; other members are not affected.",
        "Mark each partner a Builder or an Advisor. Each builder has an \"Available for board builds and reviews\" switch. Turn it off for a partner you don't want on the Sprint Board.",
        "More: /#team-and-keys",
      ],
    },
    {
      title: "How do I see what Relay costs to run?",
      items: [
        "See what Relay costs to run: ask your advisor for cloud costs. A Lead's advisor reads them live from the billing export.",
        "More: docs/cloud-costs.md",
      ],
    },
    {
      title: "How do I invite a teammate?",
      items: [
        "Only the workspace lead can invite. Open Team → Human Partners and click \"+ Invite\".",
        "Enter their email (a display name is optional) and click \"Add to Workspace\".",
        "Click \"Copy Invite Text\" and send it to them yourself. Relay doesn't send the email.",
        "They go to easyrelay.app and sign in with Google or email, using that same email address. They're in.",
        "Or share the link under Team → Workspace Invite Link (\"Copy Link\"). It expires after 7 days; \"Regenerate\" replaces it.",
        "The Free plan allows one human member. Adding more needs an upgrade.",
      ],
    },
    {
      title: "How do I set up a project?",
      items: [
        "Open Create-A-Project and click \"+ New Project\".",
        "Fill in the project name and, if agents work in a local checkout, the working directory. Click \"Create\".",
        "Open the project to add its background and features. Every feature needs an ID, a name, a description and at least one key outcome, in plain words.",
        "New projects start published. Use \"Save Draft\" to hide a project's background from agents, and \"Publish\" to show it again.",
        "For a guided interview, open the project's Requirements tab and click \"Start Structured Interview →\". It fills the Log composer with a message to @relay. Press Send to start.",
        "When @relay posts a requirements draft, an \"Import from @relay\" button appears on that tab.",
        "More: /#describe-the-project",
      ],
    },
    {
      title: "How do I write and save a spec?",
      items: [
        "Click Build-A-Spec in the left menu. A floating panel opens.",
        "Give the spec a title and describe the work: what changes, which files, which repos.",
        "Choose a project, and a feature if the project has any.",
        "Set the track type: Build, or Investigation (read-only, no code).",
        "Click \"Validate\". Fix what it flags. A soft warning doesn't block you.",
        "When it passes, click \"Save to Backlog\" and choose a project. You'll see \"Saved to backlog\" with the Log number.",
        "Not needed any more? Retire it on the Backlog page with a reason.",
        "More: /#spec-the-work",
      ],
    },
    {
      title: "How do I clean up my backlog?",
      items: [
        "Backlog page → Backlog Cleanup tab → pick a project → review each group.",
        "Relay scans the project (or opens a scan from the last day) and shows a card per group: already built, replaced, duplicates, old.",
        "For each item choose Keep, Retire… (with a reason) or Board it. Nothing is retired until you choose, and Keep, Retire… and Board it can each be undone.",
        "\"Retire all in this tab…\" retires a whole group at once with one shared reason.",
        "Mark items \"Unsure, ask AI\" to have an AI partner check only those against the code, then decide from its verdicts.",
      ],
    },
    {
      title: "How do I board and lock a sprint?",
      items: [
        "Open Control Center → Sprint Planning.",
        "Click the → arrow on a backlog item to stage it. Only items marked Now can be staged.",
        "Relay checks the staged items for conflicts as you stage them and lists them in the order they will run. Fix anything it flags.",
        "Click the Lock button (it reads \"Lock Sprint\" followed by the next number). Locking is your approval to build.",
        "The daemon must be online to lock, and the previous sprint must have no unfinished tracks.",
        "A CRITICAL track waits for your separate confirmation. After locking, open Control Center → Actions, find its \"Awaiting Authorization — CRITICAL\" card and click Authorize. \"Dismiss\" clears the card without approving.",
        "Or post a message in the Log tagged critical-authorized, track:{track name} and project:{your project}, with your approval written in it.",
        "More: /#board-it",
      ],
    },
    {
      title: "How do I follow a sprint?",
      items: [
        "Open Control Center → Sprint Board. Each track is a card.",
        "\"Queued\": waiting for an agent. \"In progress\": being built. \"QA\": built, waiting for or in independent review.",
        "\"Done\": review approved. \"Done, with notes\": approved, and the reviewer left notes you should read before merging.",
        "\"Stopped\": the track needs a person's decision, either because the builder couldn't go further or because builder and reviewer disagreed twice.",
        "When a builder stopped because the track didn't match the spec or the code, the Stopped card shows a \"What's wrong\" line in plain words.",
        "To restart a stopped track, open the Actions tab and click \"Re-queue — start fresh from main\" or \"Re-queue — continue the existing branch\".",
        "A Done card links to its pull request.",
        "More: /#build-and-review",
      ],
    },
    {
      title: "How do I handle the Actions tab?",
      items: [
        "Open Control Center → Actions. A red number on the tab means something needs you.",
        "Stopped tracks ask you to re-queue (fresh from main, or continue the branch).",
        "CRITICAL tracks ask you to \"Authorize\". \"Dismiss\" only clears the card; it doesn't authorize.",
        "Scheduler alerts have an \"Acknowledge\" button.",
        "For a blocker, click \"Resolve blocker\" and pick one: Mark resolved, Re-queue — try again, Re-queue with a note, Reassign, Close — won't fix, or Dismiss — no action needed.",
        "When nothing needs you, it says \"No open actions.\"",
      ],
    },
    {
      title: "How do I deliver finished work?",
      items: [
        "A person always does this. Relay and its AI partners never merge, deploy or release.",
        "A finished track shows \"Built · waiting for you\" and a link to its pull request.",
        "Read any reviewer notes on a \"Done, with notes\" track first.",
        "Do your project's step: for Relay, merge and deploy; for an app, merge and publish a release.",
        "Press Mark on the card, or Mark all built for the sprint, to record it as Delivered.",
        "Deliver finished work between sprints, so stacked work doesn't conflict.",
        "More: /#deliver-it",
      ],
    },
    {
      title: "How do I test a feature?",
      items: [
        "Open Test-A-Feature, pick the project, then the feature.",
        "Open a test plan, or click \"Start New Test Plan\". Tests an AI partner wrote show as Draft. Click \"Approve\" on each one you agree with.",
        "\"Start New Run\" is available once every test is approved.",
        "In the run, mark each case Pass, Fail or Skip, add a comment if you like, and click \"Save Result\".",
        "Click \"Finish Run\" when every case has a result.",
        "If every case passed, click \"Approve feature as tested\". The feature shows Verified.",
        "If a case failed, click \"Create Backlog Items for Failures\". That puts one backlog item per failed case in the backlog to spec and fix.",
        "More: /#test-the-feature",
      ],
    },
    {
      title: "How do I fix things that look stuck?",
      items: [
        "Open Control Center → Recovery. Try these in order.",
        "\"Ping Scheduler\": safe any time. It asks the scheduler to check for work.",
        "\"Fix Sprint Lock\": use it if Ping Scheduler didn't help. It resets every track in the sprint, including any being built.",
        "Track Status Override: reset one track to a status you pick.",
        "\"Clear stale alerts (24h+)\": removes alerts older than a day.",
        "\"Force all agents to idle\": only for stuck agents. Never use it while agents are building; work may be lost.",
        "\"Force close\" sprint buttons: use them when more than one sprint is active.",
      ],
    },
    {
      title: "How do I ask @relay?",
      items: [
        "Click the \"relay\" tab on the right edge of the screen to open the team chat.",
        "Type @relay and your question, then press Shift+Enter to send.",
        "Type / at the start of a line to see shortcuts.",
        "/sprint-status gives the current sprint summary and track status.",
        "/who-is-working shows which agents are busy and what they're on.",
        "More: /#at-relay",
      ],
    },
    {
      title: "How do I switch between light and dark mode?",
      items: [
        "At the bottom of the left menu, click the mode button. It shows the mode you're in now (\"Dark mode\" or \"Light mode\") and switches to the other.",
        "Relay remembers your choice on this browser. It starts in dark mode.",
      ],
    },
    {
      title: "How do I arrange my sidebar?",
      items: [
        "Arrange your sidebar: drag a link or a whole section by its grip to move it, or use Move up and Move down. Your order is yours alone and doesn't change anyone else's sidebar. Reset nav order puts it back.",
      ],
    },
    {
      title: "How do I back up my git repos?",
      items: [
        "Back up my git repos: open Backup-My-Git, click Find my gits, tick the repos, choose a backup folder (a network drive, external disk, Dropbox or Google Drive folder), then click Back up. Each backup is a new dated file; Relay keeps none of them. Restore puts a copy in a new folder next to the original. Backup-My-Git knows your computer, not the helper: switching between Relay Helper and `relay start` on the same computer keeps your backup folder, repos and history.",
      ],
    },
    {
      title: "Change production data safely",
      items: [
        "Write a script that does a dry run by default and writes only with --apply. A person runs it, never an agent. It refuses anything outside its target. It prints counts or item numbers only, never names, emails or contents. Running it twice changes nothing. In the spec, name the person's role, not a person, and treat the write as a deploy check, not a step the agent waits for.",
      ],
    },
    {
      title: "How do I find an old post in the Log?",
      items: [
        "Find an old post: type #number or a tag in the sidebar search; Relay opens it with the posts around it. Back to live returns to the latest posts.",
      ],
    },
    {
      title: "How do I close a window?",
      items: [
        "Close a window: press Esc, or click its ×. Esc closes only the window on top. If a window has been dragged out of reach, double-click its title bar to bring it back to the middle of the screen.",
      ],
    },
  ],
};
